import React, { useEffect, useRef, useState } from "react";
import apisecured from "../../api/apisecured";
import { useDispatch } from "react-redux";
import { setToast } from "../../State/toastSlice";
import * as Ryft from "@ryftpay/web";

const MERCHANT_NAME = "Sponsa";
const MERCHANT_COUNTRY_CODE = "GB";

const RyftPayButton = ({
  currency,
  userlogin,
  goaluuid,
  amountRef,
  name,
  message,
  isPrivateMessage,
  ReceipentUID,
  isGeneralDonation = false,
  guestEmail = "",
  onRequireGuestEmail,
  onSuccess,
}) => {
  const dispatch = useDispatch();

  const [creatingSession, setCreatingSession] = useState(false);
  const [paymentUiReady, setPaymentUiReady] = useState(false);
  const [cardValid, setCardValid] = useState(false);
  const [paying, setPaying] = useState(false);
  const [ryftSessionConfig, setRyftSessionConfig] = useState(null);

  const controllerRef = useRef(null);
  const cardFormRef = useRef(null);
  const googlePayRef = useRef(null);
  const applePayRef = useRef(null);

  const guestEmailRef = useRef(guestEmail);
  const messageRef = useRef(message);
  const receipentUIDRef = useRef(ReceipentUID);
  const nameRef = useRef(name);
  const isPrivateMessageRef = useRef(isPrivateMessage);

  const handledPaymentSessionIdRef = useRef(null);

  const elementIdPrefixRef = useRef(
    `ryft-${Math.random().toString(36).slice(2)}`,
  );

  const googlePayContainerId = `${elementIdPrefixRef.current}-google-pay`;
  const applePayContainerId = `${elementIdPrefixRef.current}-apple-pay`;
  const cardFormContainerId = `${elementIdPrefixRef.current}-card-form`;

  useEffect(() => {
    console.log("RyftPayButton mounted");
  }, []);

  useEffect(() => {
    messageRef.current = message;
  }, [message]);

  useEffect(() => {
    receipentUIDRef.current = ReceipentUID;
  }, [ReceipentUID]);

  useEffect(() => {
    guestEmailRef.current = guestEmail;
  }, [guestEmail]);

  useEffect(() => {
    nameRef.current = name;
  }, [name]);

  useEffect(() => {
    isPrivateMessageRef.current = isPrivateMessage;
  }, [isPrivateMessage]);

  const destroyRyftInstances = () => {
    try {
      googlePayRef.current?.unmount?.();
      googlePayRef.current?.destroy?.();

      applePayRef.current?.unmount?.();
      applePayRef.current?.destroy?.();

      cardFormRef.current?.unmount?.();
      cardFormRef.current?.destroy?.();

      controllerRef.current?.destroy?.();
    } catch (err) {
      console.warn("Ryft cleanup warning:", err);
    }

    googlePayRef.current = null;
    applePayRef.current = null;
    cardFormRef.current = null;
    controllerRef.current = null;
  };

  useEffect(() => {
    return () => {
      destroyRyftInstances();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getDisplayAmount = () => {
    const value = parseFloat(amountRef.current);
    return Number.isFinite(value) ? value : 0;
  };

  const resetRyftFormAfterFailure = () => {
    destroyRyftInstances();

    handledPaymentSessionIdRef.current = null;

    setRyftSessionConfig(null);
    setPaymentUiReady(false);
    setCardValid(false);
    setPaying(false);
  };

  const getPaymentSessionFromResult = (result) => {
    if (result?.type === "final" && result?.paymentSession) {
      return result.paymentSession;
    }

    if (result?.result?.type === "final" && result?.result?.paymentSession) {
      return result.result.paymentSession;
    }

    return (
      result?.paymentSession ||
      result?.paymentSessionResult ||
      result?.result?.paymentSession ||
      result
    );
  };

  const handlePaymentResult = (result) => {
    const paymentSession = getPaymentSessionFromResult(result);

    const status =
      paymentSession?.status ||
      result?.status ||
      result?.result?.status ||
      result?.paymentSession?.status;

    const lastError =
      paymentSession?.lastError ||
      result?.lastError ||
      result?.error ||
      result?.result?.error ||
      result?.result?.paymentSession?.lastError;

    const paymentSessionId =
      paymentSession?.id ||
      result?.paymentSessionId ||
      result?.result?.paymentSession?.id;

    if (
      paymentSessionId &&
      handledPaymentSessionIdRef.current === paymentSessionId
    ) {
      return;
    }

    if (status === "Captured") {
      if (paymentSessionId) {
        handledPaymentSessionIdRef.current = paymentSessionId;
      }

      localStorage.setItem("paymentUserName", nameRef.current?.trim() || "");

      localStorage.setItem(
        "paymentIsPrivateMessage",
        String(isPrivateMessageRef.current),
      );

      localStorage.setItem(
        "paymentSendAsAnonymous",
        String((nameRef.current?.trim() || "") === "Anonymous"),
      );

      dispatch(
        setToast({
          message: "Payment completed successfully.",
          type: "success",
        }),
      );

      if (typeof onSuccess === "function") {
        onSuccess(paymentSession);
      }

      return;
    }

    if (status === "Approved") {
      if (paymentSessionId) {
        handledPaymentSessionIdRef.current = paymentSessionId;
      }

      dispatch(
        setToast({
          message:
            "Payment was approved but not captured yet. Please wait for confirmation.",
          type: "info",
        }),
      );

      return;
    }

    if (
      status === "PendingAction" ||
      status === "RequiresAction" ||
      result?.type === "action-required" ||
      result?.type === "actionRequired" ||
      result?.result?.type === "action-required" ||
      result?.result?.type === "actionRequired"
    ) {
      dispatch(
        setToast({
          message: "Please complete payment authentication.",
          type: "info",
        }),
      );

      return;
    }

    const errorMessage =
      lastError?.displayError ||
      lastError?.message ||
      result?.userFacingErrorMessage ||
      result?.message ||
      result?.result?.userFacingErrorMessage ||
      result?.result?.message ||
      "Payment was not completed.";

    dispatch(
      setToast({
        message: errorMessage,
        type: "error",
      }),
    );

    console.error("Ryft payment incomplete:", {
      result,
      paymentSession,
      status,
      lastError,
    });

    resetRyftFormAfterFailure();
  };

  useEffect(() => {
    if (!paymentUiReady || !ryftSessionConfig) return;

    let cancelled = false;

    const mountRyftComponents = async () => {
      destroyRyftInstances();
      setCardValid(false);

      const { publicKey, clientSecret, subAccountId } = ryftSessionConfig;

      const cardContainer = document.getElementById(cardFormContainerId);
      const googlePayContainer = document.getElementById(googlePayContainerId);
      const applePayContainer = document.getElementById(applePayContainerId);

      if (!cardContainer) {
        console.error("Ryft card form container was not found.");

        dispatch(
          setToast({
            message: "Ryft payment container was not found.",
            type: "error",
          }),
        );

        return;
      }

      try {
        if (!Ryft.createController || !Ryft.createCardForm) {
          throw new Error(
            "Ryft SDK is missing createController or createCardForm. Please update @ryftpay/web.",
          );
        }

        const controller = Ryft.createController({
          publicKey,
          clientSecret,
          accountId: subAccountId,

          theme: {
            fontFamily:
              "DM Sans, Inter, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif",
            baseBackground: "#080427",
            baseBorderRadius: "14px",
            textColor1: "#FFFFFF",
            textColor2: "#A7A0C9",
          },
        });

        controllerRef.current = controller;

        controller.on?.("error", (event) => {
          console.error("Ryft controller error:", event);
          logRyftDebug("controller.error", event);
        });

        const cardForm = Ryft.createCardForm(controller, {
          displayConfig: {
            fieldLayout: "separated",
          },
          paymentFieldConfig: {
            collectNameOnCard: false,
          },
        });

        cardFormRef.current = cardForm;

        cardForm.on("validationChange", (event) => {
          setCardValid(Boolean(event?.isValid));
        });

        cardForm.on("paymentSessionResult", (event) => {
          handlePaymentResult(event?.result || event);
        });

        cardForm.on("error", (event) => {
          console.error("Ryft card form error:", event);
        });

        await cardForm.mount(`#${cardFormContainerId}`);

        if (cancelled) return;

        if (Ryft.createGooglePay && googlePayContainer) {
          try {
            const googlePay = Ryft.createGooglePay(controller, {
              merchantName: MERCHANT_NAME,
              merchantCountryCode: MERCHANT_COUNTRY_CODE,
            });

            googlePayRef.current = googlePay;

            googlePay.on("paymentSessionResult", (event) => {
              handlePaymentResult(event?.result || event);
            });

            googlePay.on("error", (event) => {
              console.warn("Ryft Google Pay error:", event);
            });

            await googlePay.mount(`#${googlePayContainerId}`);
          } catch (walletErr) {
            console.warn("Google Pay was not mounted:", walletErr);
          }
        } else {
          console.warn(
            "Ryft.createGooglePay is not available. Update @ryftpay/web if needed.",
          );
        }

        if (cancelled) return;

        if (Ryft.createApplePay && applePayContainer) {
          try {
            const applePay = Ryft.createApplePay(controller, {
              merchantName: MERCHANT_NAME,
              merchantCountryCode: MERCHANT_COUNTRY_CODE,
            });

            applePayRef.current = applePay;

            applePay.on("paymentSessionResult", (event) => {
              console.log("Ryft Apple Pay paymentSessionResult:", event);
              logRyftDebug("applePay.paymentSessionResult", event);
              handlePaymentResult(event?.result || event);
            });

            applePay.on("error", (event) => {
              console.warn("Ryft Apple Pay error:", event);
              logRyftDebug("applePay.error", event);
            });

            await applePay.mount(`#${applePayContainerId}`);
          } catch (walletErr) {
            console.warn("Apple Pay was not mounted:", walletErr);
          }
        } else {
          console.warn(
            "Ryft.createApplePay is not available. Update @ryftpay/web if needed.",
          );
        }
      } catch (err) {
        console.error("Ryft mount error:", err);

        dispatch(
          setToast({
            message:
              err?.displayError ||
              err?.message ||
              "Failed to mount Ryft payment form.",
            type: "error",
          }),
        );

        resetRyftFormAfterFailure();
      }
    };

    mountRyftComponents();

    return () => {
      cancelled = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentUiReady, ryftSessionConfig]);

  const handleShowPaymentMethods = async () => {
    if (creatingSession || paymentUiReady) return;

    const amountToSend = amountRef.current;
    const amountNumber = parseFloat(amountToSend);
    const amountInMinorUnits = Math.round(amountNumber * 100);

    if (!amountToSend || !Number.isFinite(amountNumber) || amountNumber <= 0) {
      dispatch(
        setToast({
          message: "Please enter a valid amount.",
          type: "error",
        }),
      );

      return;
    }

    const isLoggedIn = userlogin?.isAuthenticated;
    const emailValue = guestEmailRef.current?.trim();

    if (!isLoggedIn && !emailValue) {
      onRequireGuestEmail?.();

      dispatch(
        setToast({
          message: "Please enter your email to continue with Ryft.",
          type: "info",
        }),
      );

      return;
    }

    if (!isLoggedIn && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
      dispatch(
        setToast({
          message: "Please enter a valid email address.",
          type: "error",
        }),
      );

      return;
    }

    setCreatingSession(true);
    setCardValid(false);
    handledPaymentSessionIdRef.current = null;

    try {
      const { data } = await apisecured.post("ryft/create-payment-session", {
        GoalUid: goaluuid || null,
        amount: amountInMinorUnits,
        name: nameRef.current?.trim() || "Anonymous",
        message: messageRef.current?.trim() || "No message provided.",
        ReceipentUID: receipentUIDRef.current || null,
        isPrivateMessage: isPrivateMessageRef.current,
        guestEmail: userlogin?.isAuthenticated
          ? null
          : guestEmailRef.current?.trim(),
      });

      if (!data?.publicKey || !data?.clientSecret || !data?.subAccountId) {
        throw new Error("Missing Ryft payment configuration from backend.");
      }

      setRyftSessionConfig({
        publicKey: data.publicKey,
        clientSecret: data.clientSecret,
        subAccountId: data.subAccountId,
      });

      setPaymentUiReady(true);
    } catch (err) {
      console.error("Ryft init error:", err);

      dispatch(
        setToast({
          message:
            err?.response?.data?.error ||
            err?.response?.data?.ErrorMessage ||
            err?.message ||
            "Failed to initialise Ryft payment form.",
          type: "error",
        }),
      );

      resetRyftFormAfterFailure();
    } finally {
      setCreatingSession(false);
    }
  };

  const handleConfirmCardPayment = async () => {
    if (!paymentUiReady || paying || !cardFormRef.current) return;

    setPaying(true);

    try {
      const result = await cardFormRef.current.attemptPayment();
      handlePaymentResult(result);
    } catch (err) {
      console.error("Ryft card payment error:", err);

      dispatch(
        setToast({
          message:
            err?.displayError ||
            err?.message ||
            "Error processing Ryft payment.",
          type: "error",
        }),
      );

      resetRyftFormAfterFailure();
    } finally {
      setPaying(false);
    }
  };

  const currencySymbol =
    currency === "GBP"
      ? "£"
      : currency === "USD"
        ? "$"
        : currency === "EUR"
          ? "€"
          : "£";

  const displayAmount = getDisplayAmount();

  const amountLabel =
    displayAmount > 0 ? `${currencySymbol}${displayAmount.toFixed(2)}` : "";
  const logRyftDebug = async (source, payload) => {
    const safePayload = {
      source,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      paymentSessionId:
        payload?.paymentSession?.id ||
        payload?.result?.paymentSession?.id ||
        payload?.id ||
        null,
      status:
        payload?.paymentSession?.status ||
        payload?.result?.paymentSession?.status ||
        payload?.status ||
        null,
      lastError:
        payload?.paymentSession?.lastError ||
        payload?.result?.paymentSession?.lastError ||
        payload?.lastError ||
        payload?.error ||
        null,
      message:
        payload?.message ||
        payload?.displayError ||
        payload?.userFacingErrorMessage ||
        null,
    };

    console.log("[RYFT_DEBUG]", safePayload);

    try {
      await apisecured.post("ryft/debug-log", safePayload);
    } catch (e) {
      console.warn("Failed to save Ryft debug log", e);
    }
  };
  return (
    <div
      style={{ colorScheme: "none" }}
      className={isGeneralDonation ? "" : "bg-[#030021]"}
    >
      <div className="mt-2">
        <div
          className={
            paymentUiReady ? "ryft-card-shell" : "ryft-card-shell-empty"
          }
        >
          {paymentUiReady && (
            <div className="ryft-card-header">
              <div className="ryft-card-icon">R</div>

              <div>
                <p className="ryft-card-title">Secure payment</p>
                <p className="ryft-card-subtitle">
                  Pay by card, Google Pay, or Apple Pay
                </p>
              </div>

              <div className="ryft-card-badge">PCI DSS</div>
            </div>
          )}

          {paymentUiReady && (
            <div className="ryft-wallets-host">
              <div id={googlePayContainerId} className="mb-3" />
              <div id={applePayContainerId} className="mb-3" />
            </div>
          )}

          {paymentUiReady && (
            <div id={cardFormContainerId} className="ryft-card-host" />
          )}
        </div>

        {!paymentUiReady && (
          <button
            type="button"
            onClick={handleShowPaymentMethods}
            disabled={creatingSession}
            className="text-[13px] font-medium leading-[15px] tracking-[-0.02em] text-center CustomButtonAnim text-white mt-4 rounded-[25px] py-[0.8rem] w-full HoverDarker flex justify-center items-center gap-2"
            style={{
              background: creatingSession ? "#444" : "#00BFA6",
              cursor: creatingSession ? "not-allowed" : "pointer",
              minHeight: "44px",
              display: "flex",
              visibility: "visible",
              opacity: 1,
            }}
          >
            {creatingSession ? (
              <>
                Loading...
                <div className="loader0" />
              </>
            ) : (
              <>
                <span
                  style={{
                    fontFamily: "DM Sans, sans-serif",
                    fontWeight: 700,
                    fontSize: 16,
                  }}
                >
                  R
                </span>
                Pay {amountLabel} with Ryft
              </>
            )}
          </button>
        )}

        {paymentUiReady && (
          <button
            type="button"
            onClick={handleConfirmCardPayment}
            disabled={paying || !cardValid}
            className="text-[13px] font-medium leading-[15px] tracking-[-0.02em] text-center CustomButtonAnim text-white mt-4 rounded-[25px] py-[0.8rem] w-full HoverDarker flex justify-center items-center gap-2"
            style={{
              background: paying || !cardValid ? "#444" : "#00BFA6",
              cursor: paying || !cardValid ? "not-allowed" : "pointer",
              minHeight: "44px",
              display: "flex",
              visibility: "visible",
              opacity: 1,
            }}
          >
            {paying ? (
              <>
                Processing...
                <div className="loader0" />
              </>
            ) : (
              <>
                <span
                  style={{
                    fontFamily: "DM Sans, sans-serif",
                    fontWeight: 700,
                    fontSize: 16,
                  }}
                >
                  R
                </span>
                Confirm Card Payment {amountLabel}
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default RyftPayButton;
